export const meta = {
  id: '01-sounds-and-stress',
  order: 1,
  titleFi: 'Äänteet ja paino',
  titleEn: 'Sounds and stress',
  lemmaIds: ['valar-morghūlis', 'valar-dohaeris', 'mhysa'],
  exampleIds: ['got-s3e07-003', 'got-s3e06-001', 'got-s3e06-002', 'got-s3e04-006'],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_pronunciation',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Basics_1',
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes#Stress',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammar: ['letters', 'stress'],
  showLines: ['got-s3e07-003', 'got-s3e06-001', 'got-s3e06-002', 'got-s3e04-006'],
  pronunciation: { focus: 'stress', lines: ['got-s3e07-003', 'got-s3e06-001', 'got-s3e06-002'] },
  phrases: ['valar-morghūlis', 'valar-dohaeris'],
  letter: {
    fi: 'Kirjoita kumppanillesi kolme tämän viikon valyrian sanaa. Merkitse sitten jokaisen sanan painollinen tavu napauttamalla sitä. Sanat näkyvät tekstin alla tavuittain.',
    en: 'Write your partner three High Valyrian words from this week. Then mark the stressed syllable of each by tapping it. The words are shown in syllables under the text.',
  },
  scene: 'got-s3e06-1',
  // Omat virheet (docs/COURSE.md): typed card answers, the partner's letter corrections and dictation words from week 1
  ownErrors: ['card', 'letter', 'dictation'],
  // per-person stress focus (docs/COURSE.md "Harjoitellaan painoa"): from the first recordings with the stress check
  stressFocus: true,
  // Sitkeät sanat (docs/COURSE.md): a shared memory image for a word that keeps failing, from week 1
  memoryImages: true,
  // Ääntämisvalmentaja (docs/COURSE.md): offered from week 1 to a person who turned it on; its sounds are this lesson's
  coach: true,
}

# Äänteet ja paino

Korkeavalyria kirjoitetaan latinalaisilla kirjaimilla, ja suurin osa niistä ääntyy juuri niin kuin suomalainen odottaa. Suomalainen kompastuu helpoimmin **painoon**.

Äänteet, kirjaimet ja painon säännöt ovat Kieliopissa: <TopicLink id="letters">Äänteet ja kirjaimet</TopicLink> ja <TopicLink id="stress">Paino</TopicLink>. Tällä oppitunnilla harjoitellaan niitä ääneen ja kuunnellaan sarjoista.

<Line id="got-s3e07-003" />

## Tämän viikon huomiot äänteistä

**Pituus.** Pituusero on sinulle luonnollinen, joten pidä siitä kiinni myös kirjoittaessasi: viiva kuuluu sanaan samalla tavalla kuin suomen kaksoiskirjain.

**R.** Valyrian <Hv>r</Hv> on klassisessa ääntämyksessä täryäänne kuten suomen *r*. Wikin mukaan se voi olla myös pelkkä yksi napautus, varsinkin <Term id="klusiili">klusiilin</Term> jälkeen.

<Pronounce l="kirimvose" word="kirimvose" />

<SoundGuide id="r" />

**J.** <Hv>j</Hv> on ansa, koska se ei ole suomen *j*. Sarjojen aikaan vakiintunut ääntämys on kuin englannin *j* sanassa *James* [dʒ], ja niin sanovat myös Targaryenit (Peterson). Vanhempi, klassinen ääntämys on <Term id="soinnillinen">soinnillinen</Term> <Term id="palataali">palataalinen</Term> <Term id="klusiili">klusiili</Term> [ɟ], suunnilleen kuin unkarin *gy*; sen näet sanakirjan <Term id="ipa">IPA</Term>-merkinnöissä. Astaporilaiset sanovat sen kuin *ž*.

<SoundGuide id="j" />

<Note>
**Mielikuva:** <Hv>rh</Hv> on kuin kissan kehräys, *r*:n täry ilman ääntä. Vertauksen keksi Damien Halme (Academy of High Valyrian).
</Note>

<SoundGuide id="rh" />

## Pitkät vokaalit näppäimistöllä

Puhelimella kirjoitat valyriaa valyrian näppäimistöllä (Keyman). Se toimii kaikissa sovelluksissa, joten voit kirjoittaa sillä myös viestit toisillenne.

- **Ylärivi:** <Hv part>ā</Hv>, <Hv part>ē</Hv>, <Hv part>ī</Hv>, <Hv part>ō</Hv>, <Hv part>ū</Hv>, <Hv part>ȳ</Hv> ja <Hv part>ñ</Hv> ovat omina näppäiminään kirjainten yläpuolella.
- **Pyyhkäisy ylös:** pyyhkäise vokaalia ylöspäin, niin saat sen pitkän parin: *a* → <Hv part>ā</Hv>.
- **ˉ-näppäin:** napauta ˉ-näppäintä vokaalin jälkeen, niin vokaali pitenee. Toinen napautus poistaa viivan.
- **Ehdotukset:** voit jättää pituusmerkit pois, sillä näppäimistö ehdottaa sanaa pituusmerkkeineen. Kirjoita *dary*, niin ehdotuksissa on <Hv>dārys</Hv>.

Kokeile Sanakirjan haussa: kirjoita <Hv>zaldrīzes</Hv>, <Hv>kelītīs</Hv> ja <Hv>dārys</Hv> ensin itse pituusmerkkeineen ja sitten ehdotuksen avulla.

Kortin vastauksessa ehdotuksen napauttaminen lasketaan avuksi, ja oikeakin vastaus saa silloin enintään arvion ”Vaikea”. Kirjoita vastaukset itse kirjain kirjaimelta.

## Diftongit

<Note>
**Kaikki diftongit:**

- laskevat, aina raskaita: <Hv>ae</Hv>, <Hv part>āe</Hv>, <Hv part>ao</Hv>, <Hv part>āo</Hv>
- nousevat ja kevyet: <Hv part>ia</Hv>, <Hv part>ie</Hv>, <Hv>io</Hv>, <Hv part>ua</Hv>, <Hv part>ue</Hv>
- nousevat ja raskaat, kun jälkimmäinen vokaali on pitkä: <Hv part>iā</Hv>, <Hv part>iē</Hv>, <Hv part>iō</Hv>, <Hv part>uā</Hv>, <Hv>uē</Hv>

Peterson huomauttaa, että *aē* ja *aō* eivät ole <Term id="diftongi">diftongeja</Term> vaan kaksi <Term id="tavu">tavua</Term>: *a·ō*. Derek Chan (High Valyrian Tutorial) lukee listan ääneen.
</Note>

## Paino: suomalaisen ansa

Painon sääntö on Kieliopissa (<TopicLink id="stress">Paino</TopicLink>): paino lasketaan sanan lopusta, ei alusta kuten suomessa.

Sanan sisällä konsonantit jakautuvat tavuihin niin, että seuraavan tavun alkuun tulee niin monta konsonanttia kuin mahdollista. Tavu voi alkaa vokaalilla, yhdellä konsonantilla tai <Term id="klusiili">klusiililla</Term>, jota seuraa <Term id="likvida">likvida</Term> tai <Term id="sibilantti">sibilantti</Term>.

<Note>
**Paino neljällä kysymyksellä.** Derek Chan (High Valyrian Tutorial) käy säännön läpi kysymys kerrallaan:

1. Onko sana käsky? Paino on viimeisellä tavulla: <Hv>ke·lī·TĪS</Hv>.
2. Onko toiseksi viimeinen tavu raskas? Paino on siinä: <Hv>zal·DRĪ·zes</Hv>.
3. Onko toiseksi viimeinen kevyt ja sitä edeltävä raskas? Paino on kolmanneksi viimeisellä: <Hv>ki·RIM·vo·se</Hv>.
4. Muuten paino on toiseksi viimeisellä: <Hv>va·LO·ti</Hv>.

*ks*, *ts* ja *ps* aloittavat aina uuden tavun (Peterson), joten niitä edeltävä tavu jää avoimeksi: <Hv>ry·tsas</Hv>.
</Note>

Paino osuu siis aina johonkin sanan kolmesta viimeisestä tavusta. Neli- ja useampitavuisissa sanoissa se ei koskaan ole ensimmäisellä tavulla, toisin kuin suomessa.

<Pronounce l="rytsas" word="rytsas" />

<Hv>Ry·tsas</Hv>: kaksitavuisessa sanassa paino on toiseksi viimeisellä eli ensimmäisellä tavulla.

<Pronounce word="zaldrīzes" />

<Hv>zal·drī·zes</Hv>: toiseksi viimeinen tavu <Hv part>drī</Hv> on raskas, joten sääntö ei siirrä painoa kauemmas. Paino on <Hv part>DRĪ</Hv>:llä eikä ensimmäisellä tavulla, vaikka suomalaisena tekisi mieli sanoa *ZAL-drī-zes*.

<Pronounce l="kessa" word="kessa" />

<Hv>kes·sa</Hv>: kaksitavuinen sana, paino ensimmäisellä tavulla.

### Paino liikkuu, kun sana saa päätteen

Koska paino lasketaan sanan lopusta, se siirtyy, kun sanaan tulee tavun mittainen pääte (<TopicLink id="stress">Paino</TopicLink>):

<Pronounce word="vala" />

<Pronounce word="valoti" />

### Poikkeukset

Käskymuodoissa paino on aina viimeisellä tavulla. Muut poikkeukset ovat Kieliopissa (<TopicLink id="stress">Paino</TopicLink>).

Damien Halme (Academy of High Valyrian) harjoittaa käskyjen painoa näillä:

<Pronounce word="kelītīs" />

<Pronounce word="memēbātās" />

## Kuuntele sarjoista

Petersonin äänitteissä paino kuuluu selvästi. Kuuntele, toista hidastettuna ja silmukassa ja sano perässä.

<Note>
**Harjoitellaan painoa.** Kun vertaat omaa äänitystäsi Petersonin äänitteeseen samasta sanasta tai repliikistä, sovellus tarkistaa jokaisen sanan kohdalla, osuiko paino oikealle tavulle. Jos sanan paino osuu kahtena eri päivänä väärälle tavulle, sana näkyy viikon Puhu-kohdassa otsikon ”Harjoitellaan painoa” alla, ja ”Missä paino?” -harjoitus kysyy sitä ensimmäisten joukossa. Kun olet viimeisen virheen jälkeen sanonut sen kahdesti oikein, se poistuu listalta.
</Note>

<Note>
**Kuuntelukortit.** Kun olet tunnistanut sanan kolmena eri päivänä, päivän korteissa tulee vastaan myös sen kuuntelukortti: kuulet sanan näkemättä sitä. Sano merkitys ääneen, näytä vastaus ja arvioi itsesi kuten muissakin korteissa. Sanan kirjoitusasun saat näkyviin napauttamalla ”Näytä teksti”. Ääni on Petersonin. Jos Peterson ei ole äänittänyt sanaa, kuulet kumppanisi äänitteen, kunhan paino osuu siinä kohdalleen tai olette molemmat hyväksyneet sen.
</Note>

<Note>
**Kun kuuntelet sarjoja.** Moni näyttelijä sanoo <Hv part>y</Hv>:n kuin *i*:n (kuuntele vaikka Daenerysin repliikkejä), ja *r* jää usein yhdeksi napautukseksi. Peterson kertoo itsekin sanoneensa <Hv part>q</Hv>:n tavallisena *k*:na useimmissa äänitteissään (Kraznysin ja Missandein repliikkejä lukuun ottamatta). Kun kuulet sarjassa *i*:n, kirjoitusasussa voi silti olla <Hv part>y</Hv>. Sano itse *y*, *q* ja täryllinen *r*: suomalaiselle ne ovat helppoja. Näyttelijöiden ääni on kuuntelumateriaalia, ääntämisen malli on Petersonin. Tästä muistuttavat Derek Chan (High Valyrian Tutorial) ja Damien Halme (Academy of High Valyrian).
</Note>

<Line id="got-s3e06-001" />

<Line id="got-s3e06-002" />

<Line id="got-s3e04-006" />

<Note>
**Omat virheet.** Kun kirjoitat kortin vastauksen väärin, virheestä tulee seuraavana päivänä oma korttinsa: kirjoitat ensin oikean muodon ja näet sitten aiemman vastauksesi yliviivattuna sen vieressä. Samoin käy sanoille, jotka kirjoitat väärin sanelussa tai tehtävässä, ja kirjeidesi sanoille, jotka kumppanisi korjaa. Kortti jää pois, kun olet osannut sen kolmena päivänä peräkkäin.
</Note>

<Note>
**Sitkeät sanat.** Jos sama sana unohtuu kerta toisensa jälkeen, sovellus ehdottaa muistikuvaa, kun vastaatte siihen seuraavan kerran ”Uudelleen”. Keksikää suomen sana, joka kuulostaa valyrian sanalta, ja mielikuva, joka yhdistää sen sanan merkitykseen. Muistikuva näkyy teille molemmille, mutta vasta kun sana ei ole tullut mieleen – ei koskaan ennen kuin olette yrittäneet muistaa sen itse.
</Note>

<Vocab />

<Check kinds="stress" words="kepa,rytsas,azantys,kirimvose,zaldrīzes,drakarys,majaqagon,iotāptegon" forms="kelītīs:keligon,memēbātās:memēbagon" />
